import React, { useState, useEffect } from 'react';
import { Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * Admin Photo Upload Page
 * 
 * This is a simple admin interface for uploading product photos.
 * Access at: /admin/photos
 * 
 * Note: This is a basic implementation. For production, you should:
 * - Add authentication
 * - Add proper file validation
 * - Implement actual file upload to server
 */

const AdminPhotoUpload = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadStatus, setUploadStatus] = useState({});

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`);
      const data = await response.json();
      setProducts(data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const productsWithImages = products.filter(p => p.imageUrl && p.imageUrl !== '');
  const productsWithoutImages = products.filter(p => !p.imageUrl || p.imageUrl === '');

  return (
    <div className="paper-texture min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-8">
          <h1 className="font-display text-5xl mb-4" data-testid="admin-title">
            Product Photo Manager
          </h1>
          <p className="text-lg text-[#5C5855]">
            Upload and manage product photos for your store
          </p>
        </div>

        {/* Status Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white border border-alt-bg p-6">
            <div className="text-4xl font-bold text-cape-red mb-2">
              {products.length}
            </div>
            <div className="text-sm uppercase tracking-wider text-[#8C8885]">
              Total Products
            </div>
          </div>
          <div className="bg-white border border-alt-bg p-6">
            <div className="text-4xl font-bold text-sage mb-2">
              {productsWithImages.length}
            </div>
            <div className="text-sm uppercase tracking-wider text-[#8C8885]">
              With Photos
            </div>
          </div>
          <div className="bg-white border border-alt-bg p-6">
            <div className="text-4xl font-bold text-[#8C8885] mb-2">
              {productsWithoutImages.length}
            </div>
            <div className="text-sm uppercase tracking-wider text-[#8C8885]">
              Need Photos
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-alt-bg p-8 mb-8">
          <h2 className="font-display text-2xl mb-4">How to Add Photos</h2>
          <div className="space-y-3 text-[#2D2A26]">
            <p><strong>Method 1: Use the Update Script (Recommended)</strong></p>
            <ol className="list-decimal ml-6 space-y-2">
              <li>Add photos to: <code className="bg-white px-2 py-1">/app/frontend/public/images/products/</code></li>
              <li>Run: <code className="bg-white px-2 py-1">python3 /app/backend/scripts/update_product_images.py</code></li>
              <li>Refresh this page to see updates</li>
            </ol>
            <p className="mt-4"><strong>Method 2: Upload in Chat</strong></p>
            <p>Upload photos in batches to the Emergent chat, and the AI will update the database automatically.</p>
          </div>
        </div>

        {/* Products Without Images */}
        <div className="mb-12">
          <h2 className="font-display text-3xl mb-6">
            Products Needing Photos ({productsWithoutImages.length})
          </h2>
          <div className="bg-white border border-alt-bg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {productsWithoutImages.map((product) => (
                <div
                  key={product.sku}
                  className="flex items-center justify-between p-4 border-b border-alt-bg last:border-b-0"
                >
                  <div>
                    <div className="font-semibold">{product.name}</div>
                    <div className="text-sm text-[#8C8885]">
                      SKU: {product.sku} • {product.category}
                    </div>
                    <div className="text-xs text-[#8C8885] mt-1">
                      Filename: <code>{product.name.toLowerCase().replace(/ /g, '-')}.jpg</code>
                    </div>
                  </div>
                  <AlertCircle className="w-5 h-5 text-[#8C8885]" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Products With Images */}
        <div>
          <h2 className="font-display text-3xl mb-6">
            Products With Photos ({productsWithImages.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {productsWithImages.map((product) => (
              <div key={product.sku} className="bg-white border border-alt-bg overflow-hidden">
                <div className="aspect-square bg-alt-bg">
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">{product.name}</h3>
                      <p className="text-sm text-[#8C8885]">
                        SKU: {product.sku}
                      </p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-sage flex-shrink-0 ml-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Download Template */}
        <div className="mt-12 bg-white border border-alt-bg p-8 text-center">
          <h3 className="font-display text-2xl mb-4">Need the Complete List?</h3>
          <p className="text-[#5C5855] mb-6">
            Check the README file in the products folder for the complete list of filenames
          </p>
          <code className="bg-alt-bg px-4 py-2 inline-block">
            /app/frontend/public/images/products/README.md
          </code>
        </div>
      </div>
    </div>
  );
};

export default AdminPhotoUpload;
